import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Clock, AlertTriangle, Users, History, LogOut, Loader2, ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";
import { formatDistance, formatDuration } from "@/lib/geoUtils";
import { toast } from "sonner";
import { format, startOfWeek, endOfWeek, addWeeks, subWeeks, eachDayOfInterval, isSameDay } from "date-fns";
import { AddressFromCoords } from "./AddressFromCoords";

interface ActiveRow {
  id: string;
  clock_in_at: string;
  clock_in_lat: number | null;
  clock_in_lng: number | null;
  clock_in_accuracy: number | null;
  off_site_flag: boolean;
  off_site_reason: string | null;
  distance_from_job_m: number | null;
  employee_id: string;
  job?: { property_address: string } | null;
}

interface HistoryRow {
  id: string;
  clock_in_at: string;
  clock_out_at: string | null;
  clock_in_lat: number | null;
  clock_in_lng: number | null;
  clock_out_lat: number | null;
  clock_out_lng: number | null;
  duration_minutes: number | null;
  off_site_flag: boolean;
  off_site_reason: string | null;
  employee_id: string;
  crew?: { id: string; name: string } | null;
  job?: { property_address: string } | null;
}

interface ProfileLite {
  id: string;
  full_name: string | null;
  email: string | null;
}

export function TimeClockLiveBoard() {
  const { orgId } = useAuth();
  const [active, setActive] = useState<ActiveRow[]>([]);
  const [weekRows, setWeekRows] = useState<HistoryRow[]>([]);
  const [profiles, setProfiles] = useState<Record<string, ProfileLite>>({});
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const [weekAnchor, setWeekAnchor] = useState<Date>(new Date());

  const weekStart = useMemo(() => startOfWeek(weekAnchor, { weekStartsOn: 1 }), [weekAnchor]);
  const weekEnd = useMemo(() => endOfWeek(weekAnchor, { weekStartsOn: 1 }), [weekAnchor]);

  const load = async () => {
    if (!orgId) return;
    setLoading(true);

    const [{ data: activeData }, { data: weekData }] = await Promise.all([
      supabase
        .from("time_clock_entries")
        .select(
          "id, clock_in_at, clock_in_lat, clock_in_lng, clock_in_accuracy, off_site_flag, off_site_reason, distance_from_job_m, employee_id, job:jobs!confirmed_job_id(property_address)"
        )
        .eq("org_id", orgId)
        .eq("status", "active")
        .order("clock_in_at", { ascending: false }),
      supabase
        .from("time_clock_entries")
        .select(
          "id, clock_in_at, clock_out_at, clock_in_lat, clock_in_lng, clock_out_lat, clock_out_lng, duration_minutes, off_site_flag, off_site_reason, employee_id, job:jobs!confirmed_job_id(property_address)"
        )
        .eq("org_id", orgId)
        .gte("clock_in_at", weekStart.toISOString())
        .lte("clock_in_at", weekEnd.toISOString())
        .order("clock_in_at", { ascending: false }),
    ]);

    const activeRows = (activeData as any[]) || [];
    const weekHistory = (weekData as any[]) || [];
    setActive(activeRows);
    setWeekRows(weekHistory);

    // Load names for all referenced employees
    const ids = Array.from(
      new Set([...activeRows.map((r) => r.employee_id), ...weekHistory.map((r) => r.employee_id)].filter(Boolean))
    );
    if (ids.length) {
      const { data: profs } = await supabase
        .from("profiles")
        .select("id, full_name, email")
        .in("id", ids);
      const map: Record<string, ProfileLite> = {};
      (profs || []).forEach((p: any) => (map[p.id] = p));
      setProfiles(map);
    }

    setLoading(false);
  };

  useEffect(() => {
    load();
    if (!orgId) return;
    const ch = supabase
      .channel(`tce-${orgId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "time_clock_entries", filter: `org_id=eq.${orgId}` },
        () => load()
      )
      .subscribe();
    const i = window.setInterval(() => setTick((t) => t + 1), 30000);
    return () => {
      supabase.removeChannel(ch);
      window.clearInterval(i);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, weekStart.toISOString(), weekEnd.toISOString()]);

  const forceClockOut = async (id: string) => {
    const { error } = await supabase
      .from("time_clock_entries")
      .update({ clock_out_at: new Date().toISOString(), status: "closed" })
      .eq("id", id);
    if (error) {
      toast.error("Failed to clock out");
      return;
    }
    toast.success("Employee clocked out");
    load();
  };

  const nameFor = (id: string) =>
    profiles[id]?.full_name || profiles[id]?.email || `Employee ${id.slice(0, 8)}`;

  // Weekly rollup grouped by employee
  const weeklyByEmployee = useMemo(() => {
    const map = new Map<
      string,
      { employee_id: string; totalMinutes: number; offSite: number; shifts: HistoryRow[] }
    >();
    weekRows.forEach((r) => {
      if (!r.employee_id) return;
      if (!map.has(r.employee_id)) {
        map.set(r.employee_id, { employee_id: r.employee_id, totalMinutes: 0, offSite: 0, shifts: [] });
      }
      const e = map.get(r.employee_id)!;
      e.totalMinutes += r.duration_minutes || 0;
      if (r.off_site_flag) e.offSite += 1;
      e.shifts.push(r);
    });
    return Array.from(map.values()).sort((a, b) => b.totalMinutes - a.totalMinutes);
  }, [weekRows]);

  const weekDays = useMemo(() => eachDayOfInterval({ start: weekStart, end: weekEnd }), [weekStart, weekEnd]);

  const totalWeekHours = weekRows.reduce((s, r) => s + (r.duration_minutes || 0), 0) / 60;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard label="On the clock now" value={active.length} icon={<Users className="h-5 w-5" />} />
        <StatCard
          label="Off-site flagged"
          value={active.filter((a) => a.off_site_flag).length}
          icon={<AlertTriangle className="h-5 w-5 text-warning" />}
        />
        <StatCard
          label="Hours this week"
          value={totalWeekHours.toFixed(1)}
          icon={<Clock className="h-5 w-5" />}
        />
      </div>

      <Tabs defaultValue="live">
        <TabsList>
          <TabsTrigger value="live" className="gap-2">
            <Users className="h-4 w-4" /> Live ({active.length})
          </TabsTrigger>
          <TabsTrigger value="week" className="gap-2">
            <CalendarDays className="h-4 w-4" /> Weekly Timesheet
          </TabsTrigger>
          <TabsTrigger value="history" className="gap-2">
            <History className="h-4 w-4" /> All Shifts
          </TabsTrigger>
        </TabsList>

        {/* LIVE TAB */}
        <TabsContent value="live" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Live crew status</CardTitle>
              <CardDescription>Real-time view of who's on the clock and where.</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : active.length === 0 ? (
                <div className="py-12 text-center text-sm text-muted-foreground">
                  Nobody's on the clock right now.
                </div>
              ) : (
                <div className="space-y-3">
                  {active.map((row) => {
                    const minutes = Math.max(
                      0,
                      Math.floor((Date.now() - new Date(row.clock_in_at).getTime()) / 60000)
                    );
                    void tick;
                    return (
                      <div
                        key={row.id}
                        className="flex flex-col gap-2 rounded-lg border border-border bg-card p-4 sm:flex-row sm:items-start sm:justify-between"
                      >
                        <div className="min-w-0 space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="relative flex h-2 w-2">
                              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-75" />
                              <span className="relative inline-flex h-2 w-2 rounded-full bg-success" />
                            </span>
                            <span className="font-medium">{nameFor(row.employee_id)}</span>
                            {row.off_site_flag && (
                              <Badge variant="outline" className="border-warning text-warning">
                                <AlertTriangle className="mr-1 h-3 w-3" />
                                Off-site
                              </Badge>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Clock className="h-3.5 w-3.5" />
                              {formatDuration(minutes)} · since {format(new Date(row.clock_in_at), "h:mm a")}
                            </span>
                            {row.job?.property_address && (
                              <span className="truncate max-w-[280px]">📍 Job: {row.job.property_address}</span>
                            )}
                            {row.distance_from_job_m != null && (
                              <span>{formatDistance(row.distance_from_job_m)} from site</span>
                            )}
                          </div>
                          {row.clock_in_lat != null && row.clock_in_lng != null && (
                            <div className="text-xs text-muted-foreground">
                              <AddressFromCoords lat={row.clock_in_lat} lng={row.clock_in_lng} />
                              {row.clock_in_accuracy != null && (
                                <span className="ml-1 tabular-nums">· ±{Math.round(row.clock_in_accuracy)}m</span>
                              )}
                            </div>
                          )}
                          {row.off_site_reason && (
                            <div className="text-xs italic text-muted-foreground">"{row.off_site_reason}"</div>
                          )}
                        </div>
                        <Button size="sm" variant="outline" onClick={() => forceClockOut(row.id)}>
                          <LogOut className="mr-2 h-4 w-4" />
                          Force clock out
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* WEEKLY TAB */}
        <TabsContent value="week" className="mt-4 space-y-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setWeekAnchor(subWeeks(weekAnchor, 1))}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="text-sm font-medium">
                {format(weekStart, "MMM d")} – {format(weekEnd, "MMM d, yyyy")}
              </div>
              <Button variant="outline" size="sm" onClick={() => setWeekAnchor(addWeeks(weekAnchor, 1))}>
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setWeekAnchor(new Date())}>
                This week
              </Button>
            </div>
            <div className="text-sm text-muted-foreground">
              Total: <span className="font-semibold tabular-nums">{totalWeekHours.toFixed(1)}h</span>
            </div>
          </div>

          {weeklyByEmployee.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-sm text-muted-foreground">
                No shifts logged this week.
              </CardContent>
            </Card>
          ) : (
            weeklyByEmployee.map((emp) => (
              <Card key={emp.employee_id}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">{nameFor(emp.employee_id)}</CardTitle>
                    <div className="flex items-center gap-2">
                      {emp.offSite > 0 && (
                        <Badge variant="outline" className="border-warning text-warning">
                          {emp.offSite} off-site
                        </Badge>
                      )}
                      <Badge variant="secondary" className="tabular-nums">
                        {formatDuration(emp.totalMinutes)} total
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-1">
                  {/* Day-by-day mini grid */}
                  <div className="mb-3 grid grid-cols-7 gap-1 text-center text-[10px]">
                    {weekDays.map((d) => {
                      const dayMin = emp.shifts
                        .filter((s) => isSameDay(new Date(s.clock_in_at), d))
                        .reduce((sum, s) => sum + (s.duration_minutes || 0), 0);
                      return (
                        <div key={d.toISOString()} className="rounded border border-border bg-muted/30 p-1">
                          <div className="text-muted-foreground">{format(d, "EEE")}</div>
                          <div className="font-semibold tabular-nums">
                            {dayMin > 0 ? (dayMin / 60).toFixed(1) + "h" : "—"}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Detailed shifts */}
                  <div className="divide-y divide-border">
                    {emp.shifts.map((s) => (
                      <div key={s.id} className="flex flex-col gap-1 py-2 text-xs sm:flex-row sm:items-center sm:justify-between">
                        <div className="min-w-0">
                          <div className="font-medium text-foreground">
                            {format(new Date(s.clock_in_at), "EEE MMM d · h:mm a")}
                            {s.clock_out_at && ` → ${format(new Date(s.clock_out_at), "h:mm a")}`}
                            {!s.clock_out_at && <span className="ml-2 text-success">· active</span>}
                          </div>
                          <div className="text-muted-foreground">
                            Job: {s.job?.property_address || "—"}
                            {s.off_site_flag && " · off-site"}
                          </div>
                          {s.clock_in_lat != null && s.clock_in_lng != null && (
                            <div className="text-muted-foreground">
                              In: <AddressFromCoords lat={s.clock_in_lat} lng={s.clock_in_lng} showIcon={false} />
                            </div>
                          )}
                          {s.clock_out_lat != null && s.clock_out_lng != null && (
                            <div className="text-muted-foreground">
                              Out: <AddressFromCoords lat={s.clock_out_lat} lng={s.clock_out_lng} showIcon={false} />
                            </div>
                          )}
                        </div>
                        <Badge variant="outline" className="tabular-nums shrink-0">
                          {s.duration_minutes != null ? formatDuration(s.duration_minutes) : "—"}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        {/* ALL HISTORY TAB */}
        <TabsContent value="history" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>This week's shifts</CardTitle>
              <CardDescription>All clock-ins and clock-outs for the selected week.</CardDescription>
            </CardHeader>
            <CardContent>
              {weekRows.length === 0 ? (
                <div className="py-12 text-center text-sm text-muted-foreground">No shifts yet.</div>
              ) : (
                <div className="divide-y divide-border">
                  {weekRows.map((h) => (
                    <div key={h.id} className="flex items-start justify-between gap-3 py-3">
                      <div className="min-w-0 space-y-0.5">
                        <div className="font-medium">{nameFor(h.employee_id)}</div>
                        <div className="text-xs text-muted-foreground">
                          {h.job?.property_address || "—"} · {format(new Date(h.clock_in_at), "MMM d, h:mm a")}
                          {h.clock_out_at && ` → ${format(new Date(h.clock_out_at), "h:mm a")}`}
                          {h.off_site_flag && " · off-site"}
                        </div>
                        {h.clock_in_lat != null && h.clock_in_lng != null && (
                          <div className="text-xs text-muted-foreground">
                            <AddressFromCoords lat={h.clock_in_lat} lng={h.clock_in_lng} />
                          </div>
                        )}
                      </div>
                      <Badge variant="secondary" className="tabular-nums shrink-0">
                        {h.duration_minutes != null ? formatDuration(h.duration_minutes) : h.clock_out_at ? "—" : "active"}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function StatCard({ label, value, icon }: { label: string; value: number | string; icon: React.ReactNode }) {
  return (
    <Card>
      <CardContent className="flex items-center justify-between p-4">
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
          <div className="text-2xl font-bold tabular-nums">{value}</div>
        </div>
        <div className="rounded-md bg-muted p-2 text-muted-foreground">{icon}</div>
      </CardContent>
    </Card>
  );
}
